/**
 * Service tickets list — DataGrid toolbar like rents. Row → full page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import { canCreateServiceTicket, getCurrentRole } from '../utils/accessControl';
import { mapServiceTicketRows } from './serviceTicketMappers';


function useQuery() {
  const { search } = useLocation();
  try {
    return new URLSearchParams(search);
  } catch {
    return new URLSearchParams();
  }
}

export default function ServiceTicketsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const dataGridLocaleText = useDataGridLocaleText();
  const canCreate = canCreateServiceTicket(getCurrentRole());

  const stationNumber = query.get('stationNumber') || '';
  const stage = query.get('stage') || '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [] });

  const replaceQuery = useCallback((patch) => {
    const next = new URLSearchParams(history.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    const qs = next.toString();
    history.replace(qs ? `/service/tickets?${qs}` : '/service/tickets');
  }, [history]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 0, size: 100 };
      if (stage) params.stage = stage;
      if (stationNumber.trim()) params.stationNumber = stationNumber.trim();
      const { data } = await api.getServiceTickets(params);
      setRows(mapServiceTicketRows(data, t));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || e?.message || t('service.errorLoad'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [stage, stationNumber, t]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => (
      [row.id, row.shortId, row.equipmentSerial, row.typeLabel, row.stageLabel, row.assigneeEmail, row.ownerAdminEmail, row.slaStatusLabel]
        .some((v) => String(v || '').toLowerCase().includes(q))
    ));
  }, [rows, searchQuery]);

  const columns = useMemo(
    () => [
      { field: 'shortId', headerName: t('service.ticketId'), flex: 0.7, minWidth: 110 },
      {
        field: 'createdAtLabel',
        headerName: t('service.colCreated'),
        flex: 1,
        minWidth: 150,
        sortingOrder: ['desc', 'asc'],
      },
      { field: 'typeLabel', headerName: t('service.colType'), flex: 1, minWidth: 140 },
      {
        field: 'stageLabel',
        headerName: t('service.colStage'),
        flex: 0.9,
        minWidth: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            color={params.row.stage === 'DONE' ? 'success' : params.row.stage === 'REVIEW_FAILED' ? 'warning' : 'default'}
            variant={params.row.stage === 'DONE' ? 'filled' : 'outlined'}
          />
        ),
      },
      { field: 'equipmentSerial', headerName: t('service.equipmentSerial'), flex: 1, minWidth: 140 },
      { field: 'slaDueLabel', headerName: t('service.colSla'), flex: 1.1, minWidth: 150 },
      {
        field: 'slaStatusLabel',
        headerName: t('service.colOverdue'),
        flex: 0.9,
        minWidth: 140,
        renderCell: (params) => (
          params.value
            ? <TypographyLikeChip overdue label={params.value} />
            : '—'
        ),
      },
      { field: 'ownerAdminEmail', headerName: t('service.partner'), flex: 1.1, minWidth: 150 },
      { field: 'assigneeEmail', headerName: t('service.colAssignee'), flex: 1.1, minWidth: 150 },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('service.ticketsTitle')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {stationNumber ? (
              <Chip
                size="small"
                color="primary"
                label={t('service.queueStation', { number: stationNumber })}
                onDelete={() => replaceQuery({ stationNumber: null })}
              />
            ) : null}
            {canCreate && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => history.push('/service/tickets/new')}
              >
                {t('service.create')}
              </Button>
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Stack spacing={1.5} sx={{ flex: 1, width: '100%', minHeight: 520 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ flex: 1, width: '100%', minHeight: 480 }}>
            <DataGrid
              rows={visibleRows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              disableColumnResize
              density="compact"
              showToolbar
              localeText={dataGridLocaleText}
              filterModel={filterModel}
              onFilterModelChange={(model) => setFilterModel(model || { items: [] })}
              onRowClick={(params) => {
                history.push(`/service/tickets/${encodeURIComponent(params.id)}`);
              }}
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: { labelRowsPerPage: t('common.rowsPerPage') },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: (value) => setSearchQuery(String(value || '').trim()),
                  searchPlaceholder: t('service.searchTickets'),
                  loading,
                },
                filterPanel: {
                  filterFormProps: {
                    logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                    columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                    operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                    valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                  },
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                minHeight: 480,
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
              pageSizeOptions={[20, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
            />
          </Box>
        </Stack>
      </PageContainer>
    </Box>
  );
}

function TypographyLikeChip({ overdue, label }) {
  return (
    <Chip
      size="small"
      label={label}
      color={overdue ? 'error' : 'default'}
      variant="outlined"
      sx={{ height: 20, maxWidth: '100%', '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
    />
  );
}
